import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowDown, ArrowUp, ChevronsUpDown, Download, FilterX,
  ListFilter, Search, Trash2,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Drawer, Field } from '@/components/live';
import { removeRow } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { useGridToolbar } from '@/components/gridToolbar';
import { can } from '@/auth/session';
import { toast } from '@/lib/toast';
import type { Column } from '@/components/live';
import '@/styles/datagrid.css';

/**
 * The grid every list screen uses: search, per-column filters, sortable columns, pagination,
 * row numbers, a column chooser, and export. Ported from the Hub grid (owner, 2026-09-21: "data
 * table grid take it from hub project"), in plain CSS on Vanam's tokens - see datagrid.css.
 *
 * Everything here works on the rows the screen passed in (client-side). Search, filters and
 * sorting read the RAW value `row[column.key]`, never the rendered cell: a status pill or a date
 * chip renders as an element, and matching or ordering those would mean nothing.
 *
 * EXPORT IS A PERMISSION, not a button. `export` is its own action in the permission
 * catalogue, separate from `read`, precisely because taking a copy of a farmer register out of
 * the system is a different act from looking at it - a viewer who may read a screen has not
 * thereby been allowed to walk away with it. The control is hidden when the role lacks it, and
 * the server is the real enforcement: the rows exported are the rows RLS already returned, so
 * nothing can be exported that could not be seen.
 *
 * What is exported is what is ON SCREEN - the current sort, the visible columns, and the rows
 * the search and filters left (every page of them, not just the page being shown) - because a
 * file that does not match what the person was looking at is a file they will not be able to
 * explain later.
 */
export function DataGrid<T extends { id?: string | null }>({
  rows,
  columns,
  screenKey,
  exportName,
  onRowClick,
  selectedId,
  toolbar,
  pageSize: initialPageSize = 20,
  emptyMessage = 'No records found',
  search: controlledSearch,
  onSearchChange,
  searchPlaceholder,
  selectable,
  selectedIds,
  onSelectionChange,
  bulkActions,
  deleteView,
  recordNoun = 'record',
}: {
  rows: T[];
  columns: Array<Column<T>>;
  /** The screen whose `export` permission opens the download. Omit to hide it entirely. */
  screenKey?: string;
  exportName?: string;
  onRowClick?: (row: T) => void;
  selectedId?: string | null;
  toolbar?: ReactNode;
  /** Rows per page on first render. The person can change it from the footer. */
  pageSize?: number;
  /** Shown when the screen passed no rows at all. */
  emptyMessage?: string;
  /**
   * A screen that searches on the SERVER (its query already narrows `rows`) passes its term and
   * setter here, so there is one search box - the grid's - instead of two. The grid then does not
   * also filter by the term itself: the server matched on columns the grid may not even show.
   */
  search?: string;
  onSearchChange?: (term: string) => void;
  searchPlaceholder?: string;
  /**
   * Bulk actions (owner, 2026-09-21: "bulk approval option for datagrid"). Rows the predicate
   * accepts get a checkbox; the header box ticks every such row the search and filters left.
   */
  selectable?: (row: T) => boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  /** Shown above the table while at least one row is ticked. */
  bulkActions?: (ids: string[]) => ReactNode;
  /**
   * The api view to soft-delete through, which turns on deleting the ticked rows (owner,
   * 2026-09-25: "only selected rows shall be downloaded or soft deleted").
   *
   * `removeRow` and not `fn_soft_delete_record`: that RPC knows exactly four record types -
   * farmer, plot, kyc, feature - each with its own dependency checks, and a register it has never
   * heard of is refused outright. `removeRow` sets is_deleted through the api view, so the write
   * policy, the approval lock and the change reason all apply as they do to any other edit.
   */
  deleteView?: string;
  /** What one row is called in the confirmation: "3 farmers", "3 records". */
  recordNoun?: string;
}) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const { publish: publishToolbar } = useGridToolbar();
  // This grid's identity for the header handover, stable for the life of the component.
  const toolbarOwner = useRef<symbol>(Symbol('grid'));
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [hidden, setHidden] = useState<string[]>([]);
  const [chooser, setChooser] = useState(false);
  const [ownSearch, setOwnSearch] = useState('');
  const search = onSearchChange ? controlledSearch ?? '' : ownSearch;
  const setSearch = onSearchChange ?? setOwnSearch;
  const clientSearch = onSearchChange ? '' : ownSearch;
  // column key -> the raw values (as filter keys) kept; an absent or empty set keeps everything.
  const [filters, setFilters] = useState<Record<string, Set<string>>>({});
  const [openFilter, setOpenFilter] = useState<{ key: string; anchor: DOMRect } | null>(null);
  // The column filter marks show only while this is on, from the header's Filter icon. A filter
  // already set stays set when it is switched off - the "showing x of y - clear" chip still says so.
  const [filtering, setFiltering] = useState(false);
  const filterCount = Object.values(filters).filter((kept) => kept.size > 0).length;
  /*
   * Rows arrive in batches as the reader scrolls (owner, 2026-09-27: "load 50 records as batch
   * loading?", after the AppSheet sample): the first fifty, then fifty more each time the end
   * comes into view. No pager. `limit` is how many of the narrowed rows are drawn; a new search,
   * filter or sort starts it over.
   */
  const batch = Math.max(50, initialPageSize);
  const [limit, setLimit] = useState(batch);
  const setPage = (_page: number) => setLimit(batch);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const chooserRef = useRef<HTMLDivElement>(null);
  // Turned on from the header. Ticks are dropped when it is turned off, so a hidden selection
  // can never be acted on by a later click.
  const [picking, setPicking] = useState(false);
  const [ownTicked, setOwnTicked] = useState<string[]>([]);
  const [confirming, setConfirming] = useState<null | { ids: string[]; reason: string; busy: boolean }>(null);
  const [deleteError, setDeleteError] = useState<string>();
  /*
   * Column widths, in px (owner, 2026-09-27: "dont wrap text in column ... auto resize column
   * based on 80% of column values length as default ... resize column width in all pages").
   * A cell never wraps: it is one line, cut with an ellipsis, and the full text is its tooltip.
   * Each column starts at the 80th percentile of its values' widths on the page - wide enough
   * for four values in five, so one long address does not push the whole grid sideways - and
   * a column somebody has dragged keeps that width until the grid is left.
   */
  const [widths, setWidths] = useState<Record<string, number>>({});
  const dragged = useRef<Set<string>>(new Set());
  const tableRef = useRef<HTMLTableElement>(null);

  const mayExport = Boolean(screenKey) && can(profile, screenKey!, 'export');
  const mayDelete = Boolean(screenKey) && can(profile, screenKey!, 'delete');
  const shown = columns.filter((column) => !hidden.includes(column.key));

  // A column is filterable only when the rows actually carry a value under its key. A column
  // that exists purely to render (an action button, a computed chip) has nothing to filter on.
  const filterable = useMemo(() => {
    const keys = new Set<string>();
    for (const column of columns) {
      if (!column.label) continue;
      if (rows.some((row) => !isBlank(rawOf(row, column.key)))) keys.add(column.key);
    }
    return keys;
  }, [rows, columns]);

  const visible = useMemo(() => {
    const query = clientSearch.trim().toLowerCase();
    let result = rows;
    if (query) {
      result = result.filter((row) => columns.some((column) =>
        displayOf(rawOf(row, column.key)).toLowerCase().includes(query)));
    }
    for (const [key, kept] of Object.entries(filters)) {
      if (kept.size === 0) continue;
      result = result.filter((row) => kept.has(filterKey(rawOf(row, key))));
    }
    if (sort) {
      // Sorts on the underlying value, not the rendered cell: a status pill and a date chip both
      // render as elements, and sorting those would order by nothing meaningful.
      result = [...result].sort((a, b) => {
        const left = rawOf(a, sort.key);
        const right = rawOf(b, sort.key);
        if (left === right) return 0;
        if (isBlank(left)) return 1;                          // blanks last, both directions
        if (isBlank(right)) return -1;
        const compared = typeof left === 'number' && typeof right === 'number'
          ? left - right
          : displayOf(left).localeCompare(displayOf(right), 'en', { numeric: true });
        return sort.direction === 'asc' ? compared : -compared;
      });
    }
    return result;
  }, [rows, columns, clientSearch, filters, sort]);

  const first = 0;
  const pageRows = visible.slice(0, limit);
  const more = visible.length > limit;

  // The next batch, when the end of the drawn rows comes into view - in the grid's own scroll on a
  // desktop, or the page's on a phone; the observer does not mind which.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !more) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setLimit((current) => current + batch);
    }, { rootMargin: '200px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [more, batch, limit]);

  // The grid runs to the bottom of the window, as the sample does: the header stays put and the
  // horizontal bar sits at the bottom edge rather than under the last row. Not on a phone, where
  // the page scrolls, and not inside a drawer, which has its own height.
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const fit = () => {
      if (window.innerWidth <= 600 || scroll.closest('.drawer')) { scroll.style.height = ''; return; }
      const top = scroll.getBoundingClientRect().top;
      const footer = scroll.nextElementSibling?.getBoundingClientRect().height ?? 44;
      scroll.style.height = `${Math.max(240, window.innerHeight - top - footer)}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  });

  // Close the column chooser on an outside click, as the filter popover does.
  useEffect(() => {
    if (!chooser) return;
    const onDown = (event: MouseEvent) => {
      if (chooserRef.current && !chooserRef.current.contains(event.target as Node)) setChooser(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [chooser]);

  function toggleSort(key: string) {
    setSort((current) => (current?.key !== key
      ? { key, direction: 'asc' }
      : current.direction === 'asc'
        ? { key, direction: 'desc' }
        : null));                                          // third click clears it
    setPage(0);
  }

  function setFilter(key: string, kept: Set<string>) {
    setFilters((current) => ({ ...current, [key]: kept }));
    setPage(0);
  }

  function clearAll() {
    setFilters({});
    setSearch('');
    setPage(0);
  }

  function exportCsv() {
    if (!mayExport) return;
    // TICKED ROWS WIN (owner, 2026-09-25: "only selected rows shall be downloaded"). With nothing
    // ticked it is still everything the search and filters left, which is what it has always been
    // and what somebody who never turns selection on expects.
    const chosen = ticked.length > 0 ? visible.filter((row) => row.id && ticked.includes(row.id)) : visible;
    const header = shown.map((column) => csvCell(column.label));
    const body = chosen.map((row) =>
      shown.map((column) => csvCell(plainValue(row, column))).join(','));
    // A BOM, so Excel opens the Indian names and the rupee sign as UTF-8 rather than mojibake.
    // Written as an escape: as a literal it is an invisible character in the source that every
    // linter and half the editors read as a stray space.
    const csv = `\uFEFF${[header.join(','), ...body].join('\r\n')}\r\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${exportName ?? 'export'}-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${chosen.length} ${chosen.length === 1 ? recordNoun : `${recordNoun}s`}${ticked.length > 0 ? ' (the selected rows)' : ''}.`);
  }

  /**
   * Soft-delete the ticked rows, one call each.
   *
   * One at a time rather than in a batch because each row is an independent decision the database
   * may refuse on its own terms - a farmer with plots cannot be deleted, and that refusal belongs
   * to that farmer. Failing all thirty because one was refused would be worse than reporting it.
   */
  async function removeTicked() {
    if (!confirming || !deleteView) return;
    const reason = confirming.reason.trim();
    if (reason.length < 5) { setDeleteError('Give a reason of at least five characters.'); return; }
    setDeleteError(undefined);
    setConfirming({ ...confirming, busy: true });
    const failed: string[] = [];
    let done = 0;
    for (const id of confirming.ids) {
      try {
        await removeRow(deleteView, id, reason);
        done += 1;
      } catch (error) {
        failed.push(error instanceof Error ? error.message : 'refused');
      }
    }
    await queryClient.invalidateQueries();
    setTicked([]);
    setConfirming(null);
    if (done) toast.success(`Deleted ${done} ${done === 1 ? recordNoun : `${recordNoun}s`}.`);
    if (failed.length) toast.error(`${failed.length} could not be deleted: ${failed[0]}`);
  }

  const narrowed = visible.length !== rows.length;
  // Two ways in. A screen that asked for selection gets it as before; anything else gets it when
  // the reader turns it on from the header, which is what "select icon in header" means - the
  // grid cannot know in advance that somebody wants to tick rows today.
  const screenSelecting = Boolean(selectable && onSelectionChange);
  const selecting = screenSelecting || picking;
  const ticked = screenSelecting ? selectedIds ?? [] : ownTicked;
  const setTicked = (next: string[]) => (screenSelecting ? onSelectionChange?.(next) : setOwnTicked(next));
  const mayTick = (row: T) => (selectable ? selectable(row) : true);
  const tickable = selecting ? visible.filter((row) => row.id && mayTick(row)).map((row) => row.id as string) : [];
  const allTicked = tickable.length > 0 && tickable.every((id) => ticked.includes(id));
  function tick(id: string, on: boolean) {
    setTicked(on ? [...new Set([...ticked, id])] : ticked.filter((entry) => entry !== id));
  }
  const toggleColumn = (key: string) => setHidden((current) => (current.includes(key)
    ? current.filter((entry) => entry !== key) : [...current, key]));
  const columnToggles = columns.filter((column) => column.label)
    .map((column) => ({ key: column.key, label: column.label, hidden: hidden.includes(column.key) }));

  // Hand the header this grid's controls, and take them back when the grid goes away - otherwise
  // a drawer that closes leaves the header driving a grid that is no longer on screen.
  useEffect(() => {
    publishToolbar(toolbarOwner.current, {
      search,
      setSearch: (value: string) => { setSearch(value); setPage(0); },
      searchPlaceholder: searchPlaceholder ?? 'Search this table',
      columns: columnToggles,
      toggleColumn,
      exportCsv: mayExport ? exportCsv : undefined,
      selection: {
        active: selecting,
        // Turning selection off drops the ticks with it: a selection nobody can see must not
        // still be there to be acted on by the next click of Download or Delete.
        setActive: (on: boolean) => { setPicking(on); if (!on) setTicked([]); },
        count: ticked.length,
        clear: () => setTicked([]),
      },
      filtering: {
        active: filtering,
        setActive: (on: boolean) => { setFiltering(on); if (!on) setOpenFilter(null); },
        count: filterCount,
      },
      narrowed,
      clearAll,
    });
    return () => publishToolbar(toolbarOwner.current, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searchPlaceholder, JSON.stringify(columnToggles), mayExport, selecting, ticked.length, filtering, filterCount, narrowed, visible.length, rows.length]);

  const openColumn = openFilter ? columns.find((column) => column.key === openFilter.key) : undefined;

  // Size the columns from what is on the page. `scrollWidth` of a one-line, overflow-hidden cell
  // is the width its text WANTS, whatever width the column currently has, so this can measure
  // after the widths are applied as well as before: a new page, sort or filter re-measures, and
  // only the columns nobody has dragged move.
  const measureKey = `${shown.map((column) => column.key).join('|')}#${pageRows.map((row) => row.id ?? '').join(',')}`;
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const next: Record<string, number> = {};
    for (const column of shown) {
      if (dragged.current.has(column.key)) continue;
      const cells = Array.from(table.querySelectorAll<HTMLElement>(`td[data-col="${CSS.escape(column.key)}"] > .dg-cell`));
      const wanted = cells.map((cell) => cell.scrollWidth).sort((a, b) => a - b);
      const p80 = wanted.length ? wanted[Math.floor(0.8 * (wanted.length - 1))]! : 0;
      const head = table.querySelector<HTMLElement>(`th[data-col="${CSS.escape(column.key)}"] > .dg-head`);
      const headWidth = head ? head.scrollWidth + 10 : 0;   // the label, its sort and filter marks
      next[column.key] = Math.max(56, Math.min(360, p80), headWidth) + 24;   // + the cell padding
    }
    setWidths((current) => ({ ...current, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureKey]);

  // The tooltip is the full text, and only where the column is too narrow to show it.
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    for (const cell of Array.from(table.querySelectorAll<HTMLElement>('td > .dg-cell'))) {
      if (cell.scrollWidth > cell.clientWidth + 1) cell.title = cell.textContent ?? '';
      else cell.removeAttribute('title');
    }
  }, [widths, measureKey]);

  function startResize(key: string, event: React.PointerEvent<HTMLSpanElement>) {
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = widths[key] ?? handle.parentElement?.getBoundingClientRect().width ?? 120;
    handle.classList.add('active');
    handle.setPointerCapture(event.pointerId);
    const onMove = (move: PointerEvent) => {
      setWidths((current) => ({ ...current, [key]: Math.max(48, Math.round(startWidth + move.clientX - startX)) }));
    };
    const onUp = () => {
      dragged.current.add(key);
      handle.classList.remove('active');
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }

  const sized = shown.every((column) => widths[column.key]);

  return (
    <div className="dg">
      {/*
        * Search, Columns, Export and Select now live in the PAGE HEADER (owner, 2026-09-25:
        * "shift the columns and export icon to the header after search icon in the datagrid in
        * pages? Drop the search on datagrid as we have in header"). They are published to it by
        * the effect above rather than drawn here, so there is one row of controls on a page
        * instead of two. A screen's own `toolbar` stays with the grid, because it is about these
        * rows rather than about the view of them.
        */}
      {(toolbar || narrowed) && (
        <div className="dg-tools">
          {toolbar}
          {narrowed && (
            <button className="dg-btn" onClick={clearAll} title="Clear the search and every column filter" type="button">
              <FilterX size={14} /> Showing {visible.length} of {rows.length} - clear
            </button>
          )}
        </div>
      )}

      {selecting && ticked.length > 0 && (
        <div className="dg-bulk">
          <strong>{ticked.length} selected</strong>
          {mayExport && (
            <button className="dg-btn" onClick={exportCsv} type="button">
              <Download size={14} /> Download selected
            </button>
          )}
          {deleteView && mayDelete && (
            <button className="dg-btn danger" onClick={() => setConfirming({ ids: ticked, reason: '', busy: false })} type="button">
              <Trash2 size={14} /> Delete selected
            </button>
          )}
          {bulkActions?.(ticked)}
          <button className="dg-link" onClick={() => setTicked([])} type="button">Clear selection</button>
        </div>
      )}

      {/*
        * The confirmation (owner, 2026-09-25: "dialogue box? ... for confirmation"). The same
        * shape a single-row delete uses, and the same RPC behind it: softly, with a reason, never
        * erased. Deleting thirty rows is thirty of the decision deleting one is, so it asks the
        * same question and asks it once.
        */}
      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setConfirming(null)} type="button">Cancel</button>
            <button className="primary danger" disabled={confirming?.busy} onClick={() => void removeTicked()} type="button">
              {confirming?.busy ? 'Deleting...' : `Delete ${confirming?.ids.length ?? 0}`}
            </button>
          </>
        }
        onClose={() => setConfirming(null)}
        open={confirming !== null}
        subtitle="They leave every list, but are never erased: each stays in the audit history with your reason."
        title={`Delete ${confirming?.ids.length ?? 0} ${confirming?.ids.length === 1 ? recordNoun : `${recordNoun}s`}?`}
      >
        <Field hint="At least five characters. It is written to every one of these records." label="Reason" required>
          <textarea
            onChange={(event) => setConfirming((now) => (now ? { ...now, reason: event.target.value } : now))}
            placeholder="e.g. Duplicate entries from the 12 September import"
            rows={2}
            value={confirming?.reason ?? ''}
          />
        </Field>
        {deleteError && <p className="login-error" role="alert">{deleteError}</p>}
      </Drawer>

      <div className="dg-scroll" ref={scrollRef}>
        <table className={`dg-table${sized ? ' dg-sized' : ''}`} ref={tableRef}>
          {sized && (
            <colgroup>
              {selecting && <col style={{ width: 34 }} />}
              <col style={{ width: 44 }} />
              {shown.map((column) => <col key={column.key} style={{ width: widths[column.key] }} />)}
            </colgroup>
          )}
          <thead>
            <tr>
              {selecting && (
                <th className="dg-check" scope="col">
                  <input aria-label="Select all" checked={allTicked} disabled={tickable.length === 0}
                         onChange={(event) => setTicked(event.target.checked
                           ? [...new Set([...ticked, ...tickable])] : ticked.filter((id) => !tickable.includes(id)))}
                         type="checkbox" />
                </th>
              )}
              <th className="dg-num" scope="col">#</th>
              {shown.map((column) => {
                const sorted = sort?.key === column.key ? sort.direction : null;
                const filtered = (filters[column.key]?.size ?? 0) > 0;
                return (
                  <th
                    aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                    className={column.align}
                    data-col={column.key}
                    key={column.key}
                    scope="col"
                  >
                    <span aria-hidden="true" className="dg-resize" onPointerDown={(event) => startResize(column.key, event)}
                          title="Drag to resize the column" />
                    {column.label ? (
                      <span className="dg-head">
                        <button className="dg-sort" onClick={() => toggleSort(column.key)}
                                title="Sort (click again to reverse, a third time to clear)" type="button">
                          {column.label}
                          {sorted === 'asc' ? <ArrowUp size={12} />
                            : sorted === 'desc' ? <ArrowDown size={12} />
                              : <ChevronsUpDown className="dg-sort-idle" size={12} />}
                        </button>
                        {filterable.has(column.key) && (filtering || filtered) && (
                          <button
                            aria-label={`Filter ${column.label}`}
                            className={`dg-filter-btn shown${filtered ? ' active' : ''}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              const anchor = event.currentTarget.getBoundingClientRect();
                              setOpenFilter((current) => (current?.key === column.key ? null : { key: column.key, anchor }));
                            }}
                            title={filtered ? 'Filtered - click to change' : 'Filter column'}
                            type="button"
                          >
                            <ListFilter size={12} />
                          </button>
                        )}
                      </span>
                    ) : ''}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr className="dg-empty-row">
                <td colSpan={shown.length + (selecting ? 2 : 1)}>
                  {rows.length === 0 ? emptyMessage : (
                    <span className="dg-empty">
                      No rows match the search or filters.
                      <button className="dg-link" onClick={clearAll} type="button">Clear them</button>
                    </span>
                  )}
                </td>
              </tr>
            ) : pageRows.map((row, index) => (
              <tr
                className={[
                  selectedId && row.id === selectedId ? 'selected' : '',
                  onRowClick ? 'clickable' : '',
                ].filter(Boolean).join(' ') || undefined}
                key={row.id ?? `${first}-${index}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {selecting && (
                  <td className="dg-check" onClick={(event) => event.stopPropagation()}>
                    {row.id && mayTick(row) && (
                      <input aria-label="Select row" checked={ticked.includes(row.id)}
                             onChange={(event) => tick(row.id as string, event.target.checked)} type="checkbox" />
                    )}
                  </td>
                )}
                <td className="dg-num">{first + index + 1}</td>
                {shown.map((column) => (
                  <td className={column.align} data-col={column.key} key={column.key}>
                    <span className="dg-cell">
                      {column.render ? column.render(row) : cellText(rawOf(row, column.key))}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {more && <div aria-hidden="true" className="dg-more" ref={sentinelRef}>Loading more...</div>}
      </div>

      <div className="dg-footer">
        <span>
          {narrowed
            ? `${visible.length.toLocaleString()} of ${rows.length.toLocaleString()} rows`
            : `${rows.length.toLocaleString()} row${rows.length === 1 ? '' : 's'}`}
        </span>
        <span className="dg-spacer" />
        {more && <span className="dg-batch">Showing {pageRows.length.toLocaleString()} - scroll for more</span>}
      </div>

      {openFilter && openColumn && (
        <FilterPopover
          anchor={openFilter.anchor}
          columnKey={openColumn.key}
          kept={filters[openColumn.key] ?? new Set()}
          label={openColumn.label}
          onChange={(kept) => setFilter(openColumn.key, kept)}
          onClose={() => setOpenFilter(null)}
          rows={rows}
        />
      )}
    </div>
  );
}

/**
 * The per-column filter: a checklist of the distinct values in that column, with its own search.
 * Fixed-positioned against the header button, because the grid's scroll wrapper would clip an
 * absolutely-positioned menu. It closes on an outside click, Escape, or a scroll/resize (the
 * anchor would otherwise drift away from the header it belongs to).
 */
function FilterPopover<T>({
  rows, columnKey, label, kept, onChange, onClose, anchor,
}: {
  rows: T[];
  columnKey: string;
  label: string;
  kept: Set<string>;
  onChange: (kept: Set<string>) => void;
  onClose: () => void;
  anchor: DOMRect;
}) {
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const values = useMemo(() => {
    const seen = new Map<string, string>();                 // filter key -> label
    for (const row of rows) {
      const raw = rawOf(row, columnKey);
      const key = filterKey(raw);
      if (!seen.has(key)) seen.set(key, isBlank(raw) ? '(empty)' : displayOf(raw));
    }
    return [...seen.entries()].sort(([a, left], [b, right]) => {
      if (a === '') return 1;
      if (b === '') return -1;
      return left.localeCompare(right, 'en', { numeric: true });
    });
  }, [rows, columnKey]);

  const listed = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? values.filter(([, text]) => text.toLowerCase().includes(q)) : values;
  }, [values, query]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    const onMove = (event: Event) => {
      if (ref.current && event.target instanceof Node && ref.current.contains(event.target)) return;
      onClose();
    };
    // Deferred, so the click that opened the popover is not also the click that closes it.
    const timer = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [onClose]);

  const allKept = listed.length > 0 && listed.every(([key]) => kept.has(key));
  const someKept = listed.some(([key]) => kept.has(key));

  function toggle(key: string) {
    const next = new Set(kept);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(next);
  }

  // Keep the popover on screen at phone width: never past the right edge.
  const width = 240;
  const left = Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8));

  return (
    <div aria-label={`Filter ${label}`} className="dg-menu dg-filter" ref={ref} role="dialog"
         style={{ top: anchor.bottom + 4, left, width }}>
      <label className="dg-filter-search">
        <Search size={12} />
        <input
          autoFocus
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search values"
          type="search"
          value={query}
        />
      </label>
      <div className="dg-filter-all">
        <label>
          <input
            checked={allKept}
            onChange={() => {
              const next = new Set(kept);
              for (const [key] of listed) { if (allKept) next.delete(key); else next.add(key); }
              onChange(next);
            }}
            ref={(element) => { if (element) element.indeterminate = !allKept && someKept; }}
            type="checkbox"
          />
          Select all
        </label>
        {kept.size > 0 && (
          <button className="dg-link" onClick={() => onChange(new Set())} type="button">Clear</button>
        )}
      </div>
      <div className="dg-filter-list">
        {listed.length === 0 ? <p className="dg-filter-none">No values</p> : listed.map(([key, text]) => (
          <label key={key} title={text}>
            <input checked={kept.has(key)} onChange={() => toggle(key)} type="checkbox" />
            <span>{text}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function rawOf<T>(row: T, key: string): unknown {
  return (row as Record<string, unknown>)[key];
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/** How a raw value reads in search and in the filter list. */
function displayOf(value: unknown): string {
  if (isBlank(value)) return '';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (Array.isArray(value)) return value.map(displayOf).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * A cell with no renderer of its own. A number is shown to at most three decimals (owner,
 * 2026-09-27: "limit the decimals to 3 digits") - a Postgres numeric arrives as a string, so a
 * string that is only a number is treated as one - and never with the trailing zeros a raw
 * "3.8823000" carries.
 */
function cellText(value: unknown): string {
  if (isBlank(value)) return '--';
  const numeric = typeof value === 'number' ? value
    : typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim()) ? Number(value) : null;
  if (numeric !== null && Number.isFinite(numeric)) {
    return numeric.toLocaleString('en-IN', { maximumFractionDigits: 3 });
  }
  return displayOf(value);
}

/** The identity a filter checkbox stands for. Blank (null, undefined, '') is one bucket. */
function filterKey(value: unknown): string {
  if (isBlank(value)) return '';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}


/**
 * The underlying value for the file. A rendered cell can be a status pill or a button, and
 * neither belongs in a spreadsheet - so export reads the row, not the render.
 */
function plainValue<T>(row: T, column: Column<T>): string {
  const value = (row as Record<string, unknown>)[column.key];
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** RFC 4180: quote when the value contains a comma, a quote or a newline; double the quotes. */
function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}
